import { useCallback, useLayoutEffect, useState } from 'react'
import { flowCanvasHeight, MIN_FLOW_CANVAS_HEIGHT } from '@/lib/flowCanvasHeight'

/**
 * Measures how tall a flow canvas may be to reach the bottom of the viewport
 * from wherever it has landed on the page, floored at `MIN_FLOW_CANVAS_HEIGHT`.
 *
 * Returns a callback ref rather than a `useRef`, so the first measurement runs
 * the moment the element attaches — a `useRef` is still null on the effect pass
 * that follows the first render of a conditionally-rendered canvas.
 *
 * Re-measures on window resize and on any change to the document's own size,
 * which is what catches the banners above the canvas (error, "Flow saved",
 * the frozen-version notice) appearing and disappearing: those move the canvas
 * down or up without resizing the window.
 *
 * Deliberately NOT re-measured on scroll. Where the viewport is tall enough
 * there is nothing to scroll, and where it is not (the floor is in force) the
 * page scrolls as it always has — recomputing against a scrolled
 * `getBoundingClientRect().top` there would make the canvas grow as the user
 * scrolls down, which reads as jitter rather than as fitting.
 */
export function useFlowCanvasHeight<T extends HTMLElement>(): {
  ref: (node: T | null) => void
  height: number
} {
  const [node, setNode] = useState<T | null>(null)
  const [height, setHeight] = useState(MIN_FLOW_CANVAS_HEIGHT)

  const measure = useCallback(() => {
    if (!node) return
    setHeight(flowCanvasHeight(window.innerHeight, node.getBoundingClientRect().top))
  }, [node])

  useLayoutEffect(() => {
    if (!node) return
    measure()

    window.addEventListener('resize', measure)
    // Applying the measured height changes the document height, which fires
    // this again — it settles on the second pass because the measurement is
    // then unchanged and React bails out of the identical state update.
    const observer = new ResizeObserver(measure)
    observer.observe(document.body)

    return () => {
      window.removeEventListener('resize', measure)
      observer.disconnect()
    }
  }, [node, measure])

  return { ref: setNode, height }
}
